<template>
    <StandardLayout>
        <div class="admin-dashboard">
            <DoubleHeader :title="$t('admin.dashboard.heading')" />

            <div class="section-grid">
                <router-link :to="{ name: 'adminUsers' }" class="section-card">
                    <div class="section-card-icon">
                        <i class="icon-icon-account" />
                    </div>
                    <div class="section-card-body">
                        <h3>{{ $t("admin.dashboard.users") }}</h3>
                        <p>{{ $t("admin.dashboard.usersDescription") }}</p>
                    </div>
                    <span class="section-card-arrow">&rsaquo;</span>
                </router-link>

                <router-link :to="{ name: 'adminStations' }" class="section-card">
                    <div class="section-card-icon">
                        <i class="icon-icon-stations" />
                    </div>
                    <div class="section-card-body">
                        <h3>{{ $t("admin.dashboard.stations") }}</h3>
                        <p>{{ $t("admin.dashboard.stationsDescription") }}</p>
                    </div>
                    <span class="section-card-arrow">&rsaquo;</span>
                </router-link>

                <router-link :to="{ name: 'adminModeration' }" class="section-card">
                    <div class="section-card-icon">
                        <i class="icon-icon-notification" />
                    </div>
                    <div class="section-card-body">
                        <h3>{{ $t("admin.moderation") }}</h3>
                        <p>{{ $t("admin.dashboard.moderationDescription") }}</p>
                    </div>
                    <span class="section-card-arrow">&rsaquo;</span>
                </router-link>
            </div>

            <div class="cards-row">
                <div class="card card-server" v-if="status">
                    <h3 class="card-title">{{ $t("admin.dashboard.serverDetails") }}</h3>
                    <div class="details-grid">
                        <div class="card-row">
                            <span class="detail-label">{{ $t("admin.status.server") }}</span>
                            <span class="detail-value">{{ status.serverName }}</span>
                        </div>
                        <div class="card-row">
                            <span class="detail-label">{{ $t("admin.status.tag") }}</span>
                            <span class="detail-value">{{ status.tag }}</span>
                        </div>
                        <div class="card-row">
                            <span class="detail-label">{{ $t("admin.status.name") }}</span>
                            <span class="detail-value">{{ status.name }}</span>
                        </div>
                        <div class="card-row">
                            <span class="detail-label">{{ $t("admin.status.git") }}</span>
                            <span class="detail-value mono">{{ status.git.hash }}</span>
                        </div>
                    </div>

                    <h4 class="card-title">{{ $t("admin.serverLogs.heading") }}</h4>
                    <div class="log-links">
                        <a
                            href="https://code.conservify.org/logs-viewer/?range=3600&query=tag:fkprd%20OR%20tag:fkdev"
                            target="_blank"
                            class="card-row log-link"
                        >
                            <span>{{ $t("admin.serverLogs.all") }}</span>
                            <span class="section-card-arrow external-icon">&rsaquo;</span>
                        </a>
                        <a
                            href="https://code.conservify.org/logs-viewer/?range=86400&query=zaplevel:error"
                            target="_blank"
                            class="card-row log-link"
                        >
                            <span>{{ $t("admin.serverLogs.errors") }}</span>
                            <span class="section-card-arrow external-icon">&rsaquo;</span>
                        </a>
                        <a
                            href="https://code.conservify.org/logs-viewer/?range=86400&query=_exists_:data_processed&include=device_id,user_id,meta_errors,data_errors,meta_processed,data_processed,blocks,station_name"
                            target="_blank"
                            class="card-row log-link"
                        >
                            <span>{{ $t("admin.serverLogs.ingestions") }}</span>
                            <span class="section-card-arrow external-icon">&rsaquo;</span>
                        </a>
                        <a
                            href="https://code.conservify.org/logs-viewer/?range=86400&query=message:%22station%20conflict%22"
                            target="_blank"
                            class="card-row log-link"
                        >
                            <span>{{ $t("admin.serverLogs.conflicts") }}</span>
                            <span class="section-card-arrow external-icon">&rsaquo;</span>
                        </a>
                    </div>
                </div>

                <div class="card">
                    <h3 class="card-title">{{ $t("admin.dashboard.backupUpload") }}</h3>
                    <form @submit.prevent="saveForm" class="backup-form" v-if="!busy">
                        <label class="file-input-label">
                            <input type="file" @change="upload" class="file-input" />
                            <span class="file-input-trigger">{{ $t("admin.dashboard.chooseFile") }}</span>
                            <span class="file-input-name">{{ form.file ? form.file.name : $t("admin.dashboard.noFileChosen") }}</span>
                        </label>
                        <button class="button-solid" type="submit" :disabled="!form.file">
                            {{ $t("admin.dashboard.uploadButton") }}
                        </button>
                    </form>
                    <Spinner v-if="busy" />

                    <div v-if="uploaded" class="upload-result">
                        <div v-if="uploaded.errors" class="result-row result-error">
                            <span class="result-label">{{ $t("admin.dashboard.errors") }}</span>
                            <span class="result-value">{{ uploaded.errors }}</span>
                        </div>
                        <div v-if="uploaded.deviceId" class="result-row">
                            <span class="result-label">{{ $t("admin.dashboard.deviceId") }}</span>
                            <span class="result-value">{{ uploaded.deviceId }}</span>
                        </div>
                        <div v-if="uploaded.generationId" class="result-row">
                            <span class="result-label">{{ $t("admin.dashboard.generationId") }}</span>
                            <span class="result-value">{{ uploaded.generationId }}</span>
                        </div>
                        <div v-if="uploaded.deviceName" class="result-row">
                            <span class="result-label">{{ $t("admin.dashboard.deviceName") }}</span>
                            <span class="result-value">{{ uploaded.deviceName }}</span>
                        </div>
                        <div v-if="uploaded.records" class="result-row">
                            <span class="result-label">{{ $t("admin.dashboard.records") }}</span>
                            <span class="result-value">{{ uploaded.records }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </StandardLayout>
</template>

<script lang="ts">
import Vue from "vue";
import StandardLayout from "../StandardLayout.vue";
import CommonComponents from "@/views/shared";
import { PortalDeployStatus } from "@/api";

type Uploaded = {
    deviceId: string;
    generationId: string;
    deviceName: string;
    errors: [string];
    records: [number];
};

export default Vue.extend({
    name: "AdminMain",
    components: {
        StandardLayout,
        ...CommonComponents,
    },
    data(): {
        status: PortalDeployStatus | null;
        form: { file: any };
        busy: boolean;
        uploaded: Uploaded | null;
    } {
        return {
            status: null,
            form: { file: null },
            busy: false,
            uploaded: null,
        };
    },
    async mounted(): Promise<void> {
        await this.$services.api.getStatus().then((status) => {
            this.status = status;
        });
    },
    methods: {
        upload(this: any, ev) {
            const file = ev.target.files[0];
            this.form.file = file;
        },
        async saveForm() {
            this.busy = true;
            await this.$services.api
                .adminUploadBackup(this.form)
                .then((uploaded: any) => {
                    this.uploaded = uploaded;
                })
                .finally(() => {
                    this.busy = false;
                });
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/variables";
@use "src/scss/mixins";

.admin-dashboard {
    display: flex;
    flex-direction: column;
    padding: 40px 90px;
    max-width: 1080px;
    text-align: left;

    @include mixins.bp-down(variables.$lg) {
        padding: 40px 60px;
    }

    @include mixins.bp-down(variables.$md) {
        padding: 30px 30px;
    }
}

.section-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-top: 30px;

    @include mixins.bp-down(variables.$md) {
        grid-template-columns: 1fr;
    }
}

.section-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 22px 24px;
    background: #fff;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    text-decoration: none;
    color: var(--color-dark);
    transition: box-shadow 0.15s ease, transform 0.15s ease;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        transform: translateY(-2px);
    }

    h3 {
        font-family: var(--font-family-bold);
        font-size: 16px;
        margin: 0 0 4px;
    }

    p {
        font-size: 13px;
        color: #6a6d71;
        margin: 0;
        line-height: 1.4;
    }
}

.section-card-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(27, 128, 201, 0.08);
    border-radius: 8px;
    font-size: 20px;

    .icon-icon-account:before {
        color: #1b80c9;
    }
}

.section-card-body {
    flex: 1;
    min-width: 0;
}

.section-card-arrow {
    flex-shrink: 0;
    font-size: 24px;
    color: var(--color-border);
    line-height: 1;
    transition: color 0.15s ease;

    .section-card:hover & {
        color: var(--color-primary);
    }
}

.cards-row {
    display: grid;
    grid-template-columns: 3fr 2fr;
    gap: 20px;
    margin-top: 30px;

    @include mixins.bp-down(variables.$md) {
        grid-template-columns: 1fr;
    }
}

.card {
    background: #fff;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    padding: 24px;
}

.card-title {
    font-family: var(--font-family-bold);
    font-size: 16px;
    margin: 0 0 15px;

    &:not(:first-child) {
        margin-top: 15px;
    }
}

.backup-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.file-input {
    display: none;
}

.file-input-label {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
}

.file-input-trigger {
    display: inline-block;
    padding: 8px 16px;
    font-size: 13px;
    font-family: var(--font-family-medium);
    background: #f4f5f7;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    white-space: nowrap;
    transition: background 0.15s ease;

    &:hover {
        background: #ecedf0;
    }
}

.file-input-name {
    font-size: 13px;
    color: #6a6d71;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.button-solid {
    align-self: flex-start;
    padding: 10px 24px;
    font-size: 14px;
    font-family: var(--font-family-bold);
    background-color: var(--color-secondary);
    color: #fff;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: opacity 0.15s ease;
    width: 200px;
    margin-top: 10px;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        background: var(--color-border);
        cursor: not-allowed;
    }
}

.upload-result {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.result-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 14px;

    &.result-error {
        color: var(--color-danger);
    }
}

.result-label {
    font-family: var(--font-family-bold);
    white-space: nowrap;
    font-size: 13px;
}

.result-value {
    color: #6a6d71;
    word-break: break-all;
}

.log-links {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.card-row {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    font-size: 14px;
    color: var(--color-dark);
}

.log-link {
    text-decoration: none;
    border-radius: 3px;
    transition: background 0.12s ease;

    .external-icon {
        font-size: 24px;
        margin-left: 8px;
        color: var(--color-border);
        transition: color 0.15s ease;
    }

    &:hover {
        background: #f4f5f7;

        .external-icon {
            color: var(--color-primary);
        }
    }
}

.detail-label {
    font-size: 13px;
    color: #6a6d71;
}

.detail-value {
    font-size: 14px;
    font-family: var(--font-family-medium);
    word-break: break-all;
    margin-left: 10px;

    &.mono {
        font-family: monospace;
        font-size: 13px;
    }
}

.pagination-container ::v-deep .button {
    margin-top: -4px;
}
</style>
